/**
 * Upgrade saved workspaces to the current schema, so progress survives app
 * updates. Add one step per schema version; never edit an old step.
 */

import { SCHEMA_VERSION, emptyWorkspace, type Workspace } from './schema.js';

type Step = (old: Record<string, unknown>) => Record<string, unknown>;

/** STEPS[n] upgrades a version-n workspace to version n+1. */
const STEPS: Record<number, Step> = {};

export class WorkspaceFormatError extends Error {}

/**
 * Validate and upgrade an unknown value (from storage or an imported file) into
 * a current Workspace. Throws WorkspaceFormatError for anything that is not a
 * Tyche workspace or comes from a newer app version.
 */
export function migrate(raw: unknown): Workspace {
  if (!raw || typeof raw !== 'object') throw new WorkspaceFormatError('Not a workspace file.');
  let obj = raw as Record<string, unknown>;
  let version = typeof obj.schemaVersion === 'number' ? obj.schemaVersion : NaN;
  if (!Number.isInteger(version) || version < 1) {
    throw new WorkspaceFormatError('This file is not a Tyche Business workspace.');
  }
  if (version > SCHEMA_VERSION) {
    throw new WorkspaceFormatError('This workspace was saved by a newer version of the app. Reload the page to update, then try again.');
  }
  while (version < SCHEMA_VERSION) {
    const step = STEPS[version];
    if (!step) throw new WorkspaceFormatError(`No upgrade path from version ${version}.`);
    obj = step(obj);
    version++;
    obj.schemaVersion = version;
  }

  // Fill any missing collections with defaults so older or hand-edited files
  // cannot crash the app on a missing array.
  const base = emptyWorkspace();
  const ws = { ...base, ...obj } as Workspace;
  ws.profile = (obj.profile as Workspace['profile']) ?? {};
  ws.intake = { ...base.intake, ...(obj.intake as object | undefined) };
  for (const k of ['docsOnHand', 'documents', 'scenarios', 'snapshots'] as const) {
    if (!Array.isArray(ws[k])) (ws as unknown as Record<string, unknown>)[k] = [];
  }
  if (!ws.resolutions || typeof ws.resolutions !== 'object') ws.resolutions = {};
  if (!ws.checklist || typeof ws.checklist !== 'object') ws.checklist = {};
  return ws;
}
